/**
 * Ported VERBATIM from `apr_template/src/pages/FrameworkDesigner.tsx`.
 *
 * Only import paths change. The markup, class names, copy, state and logic are the
 * source's — the stylesheet is copied alongside, and the global classes it also uses
 * resolve because the screen renders inside `AprScreen` (`.apr-app`).
 *
 * Do not "tidy" anything here. A difference is a difference from the source.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch } from '../compat/api';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './FrameworkDesigner.css';
import {
  ASPECTS,
  ASPECT_COLOR,
  ASPECT_HINT,
  ASPECT_OWNER,
  effectiveAspect,
  type Aspect,
} from '../aspects';

/* ============================================================================
 * Framework Designer — Kanban board for composing a framework's scoring model.
 *
 * Data model: components belong to ONE dimension globally
 * (components.dimensionId). A framework selects WHICH dimensions it includes
 * (framework_dimensions). The board renders the selected framework's dimensions
 * as COLUMNS; the GLOBAL components whose dimensionId matches become the CARDS
 * in each column. Dragging a card to another column reassigns that component's
 * dimension FOR THIS FRAMEWORK, saved as soon as the card is dropped. Native HTML5 DnD only.
 *
 * A framework is LOCKED when it is in use (apr_model_count > 0). While locked,
 * dimension edits are disabled: no add/remove dimension and no card dragging.
 * ==========================================================================*/

// -- Help ---------------------------------------------------------------------

const FrameworkDesignerHelp: Help = {
  title: 'Framework Designer',
  icon: '🧩',
  purpose:
    'A visual <strong>Kanban board</strong> for composing a scoring framework. Each column is a <strong>dimension</strong> the framework includes; each card is a <strong>component</strong> that belongs to that dimension.',
  doHere: [
    'Pick a <strong>Framework</strong> from the selector to load its dimensions and components.',
    'Use <strong>+ Add Dimension</strong> to bring one of your global dimensions into this framework.',
    '<strong>Drag component cards</strong> between columns to reassign them. This changes the placement <strong>in this framework only</strong> — the same component can sit under a different dimension in another framework, and moving it here leaves those alone.',
    'Each column carries a <strong>who answers this</strong> selector. It shows the dimension’s own aspect as <em>inherited</em>; choosing another sets an override <strong>for this framework only</strong>, which is how one dimension can be answered by different owners in different frameworks.',
    'Use <strong>+ Add Component</strong> (toolbar or per-column) to create a new component in a dimension.',
    'Click the <strong>eye</strong> on a card to preview that component’s questions, or the <strong>trash</strong> to delete it.',
    'Moves <strong>save as soon as you drop a card</strong> — there is nothing to press. <strong>Save Layout</strong> stays as a retry if a save ever fails.',
  ],
  fits: 'Framework &rarr; Framework Designer',
  firstTime:
    'Select a framework, then drag a card between columns — the move is saved straight away and shows in Framework Builder.',
  sections: [
    {
      type: 'html',
      label: 'Placement belongs to the framework, not the component',
      html:
        '<p style="margin:0 0 8px">A component lives in one <strong>library</strong>, but each framework decides which dimension it sits under. So “Operational Costs” can be part of <em>Business Case</em> in one framework and <em>Business Value</em> in another, and dragging it here changes only the framework on screen.</p>' +
        '<p style="margin:0">This matters because the dimension decides <strong>who is asked</strong>: every dimension names a responsible aspect, the questionnaire is split along those lines, and each owner is sent only their own part. Moving a component to a different dimension can therefore change which person answers its questions.</p>',
    },
    {
      type: 'defs',
      label: 'Who answers a dimension',
      items: [
        { term: 'Technical', color: '#1565c0', def: 'The <strong>Application Owner</strong> — the architect; the application as a system.' },
        { term: 'Operational', color: '#00695c', def: 'The <strong>Technical Owner / Custodian</strong> — run and maintain: platform, patching, availability.' },
        { term: 'Business', color: '#6a1b9a', def: 'The <strong>Business Process Owner</strong> — the process the application serves.' },
        { term: 'Security', color: '#c62828', def: 'The <strong>Security Owner</strong> — posture, controls and compliance.' },
        { term: 'Strategy', color: '#e65100', def: 'The <strong>Executive Owner</strong>, also called the sponsor — its future and its funding.' },
      ],
      note: 'Set the default on a dimension in <strong>Framework Builder</strong>; override it for one framework using the selector on a column here. An assessment is only scored once <strong>every</strong> aspect has answered.',
    },
    {
      type: 'defs',
      label: 'Component weight',
      items: [
        { term: '1', color: '#202124', def: 'Lowest weight in the dimension average.' },
        { term: '2', color: '#155e75', def: 'Below-average influence.' },
        { term: '3', color: '#1e3a8a', def: 'Default / balanced weight.' },
        { term: '4', color: '#4c1d95', def: 'Above-average influence.' },
        { term: '5', color: '#7f1d1d', def: 'Highest weight in the dimension average.' },
      ],
      note: 'A framework in use is <strong>locked</strong> — dimension edits and card dragging are disabled.',
    },
  ],
};

// -- Types --------------------------------------------------------------------

interface FrameworkOption {
  id: string;
  name: string;
  code: string;
  locked: boolean; // apr_model_count > 0
}

interface CardQuestion {
  id: string;
  text: string;
  options: Array<{ id: string; text: string; score: number }>;
}

interface Card {
  id: string;
  name: string;
  weight: number; // 1..5
  dimensionId: string;
  questions: CardQuestion[];
}

interface Column {
  mappingId: string; // framework_dimensions.id (for removal)
  dimensionId: string;
  name: string;
  cards: Card[];
  /** The dimension's own aspect, from the global library. */
  aspect: Aspect;
  /**
   * This framework's override, or null to inherit. Kept SEPARATE from `aspect` rather than
   * pre-resolved, because the board has to show which of the two is in force — a column
   * reading "Security" tells you nothing about whether every other framework agrees.
   */
  aspectOverride: Aspect | null;
}

interface DimOption {
  id: string;
  name: string;
  /** The library's aspect, so the picker says who a dimension will bring in before it is added. */
  aspect: Aspect;
}

type Toast = { type: 'success' | 'error'; message: string } | null;

// Fixed 1..5 weight color scale (square badges)
const WEIGHT_COLORS: Record<number, string> = {
  1: '#64748b',
  2: '#0891b2',
  3: '#2563eb',
  4: '#7c3aed',
  5: '#dc2626',
};

const weightColor = (w: number): string => WEIGHT_COLORS[w] || '#64748b';

// ---------------------------------------------------------------------------
// Low-level API helper (mirrors FrameworkBuilder)
// ---------------------------------------------------------------------------

const apiCall = async (url: string, method: string, body?: unknown): Promise<any> => {
  const res = await authFetch(url, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) {
    let msg = res.statusText;
    try {
      const data = await res.json();
      msg = data?.error || data?.message || msg;
    } catch {
      // ignore parse failure
    }
    throw new Error(msg || `Request failed (${res.status})`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
};

// ---------------------------------------------------------------------------
// Normalizers
// ---------------------------------------------------------------------------

const toQuestion = (raw: any): CardQuestion => ({
  id: raw?.id != null ? String(raw.id) : '',
  text: typeof raw?.text === 'string' ? raw.text : '',
  options: Array.isArray(raw?.options)
    ? raw.options.map((o: any) => ({
        id: o?.id != null ? String(o.id) : '',
        text: typeof o?.text === 'string' ? o.text : '',
        score: Number(o?.score) || 0,
      }))
    : [],
});

const toCard = (raw: any, dimensionId: string): Card => ({
  id: String(raw?.id),
  name: typeof raw?.name === 'string' ? raw.name : '',
  weight: Number(raw?.weight) || 3,
  dimensionId,
  questions: Array.isArray(raw?.questions) ? raw.questions.map(toQuestion) : [],
});

/*
 * Build the board from the framework's OWN placement.
 *
 * `frameworkComponents` is `GET /api/frameworks/:id`'s components array, each row carrying
 * the `dimension_id` THIS framework puts it under. That used to be ignored, because
 * `framework_components` held no rows and the board was assembled from the global library
 * instead — so every framework using a dimension showed the same cards under it, and moving
 * one moved them all. Since migration 0085 the placement is per framework and this is where
 * it is read.
 *
 * The global tree is still needed for one thing: the QUESTIONS under each component, which
 * hang off the component itself and are the same wherever it is placed.
 */
const buildColumns = (frameworkDims: any[], dimsTree: any[], frameworkComponents: any[]): Column[] => {
  const treeById = new Map<string, any>(dimsTree.map((d) => [String(d.id), d]));
  const componentById = new Map<string, any>();
  for (const d of dimsTree) {
    for (const c of Array.isArray(d?.components) ? d.components : []) {
      componentById.set(String(c.id), c);
    }
  }
  const byDimension = new Map<string, any[]>();
  for (const fc of frameworkComponents) {
    const key = String(fc.dimension_id ?? '');
    const list = byDimension.get(key) ?? [];
    // Merge the framework's placement with the component's own content: the name and weight
    // are the component's, the ORDER is this framework's.
    list.push({ ...(componentById.get(String(fc.id)) ?? {}), ...fc, display_order: fc.display_order });
    byDimension.set(key, list);
  }

  return frameworkDims.map((fd) => {
    const dimensionId = String(fd.id);
    const tree = treeById.get(dimensionId);
    const rawComponents: any[] = (byDimension.get(dimensionId) ?? []).sort(
      (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0),
    );
    return {
      mappingId: fd.mapping_id != null ? String(fd.mapping_id) : '',
      dimensionId,
      name: typeof fd.name === 'string' ? fd.name : tree && typeof tree.name === 'string' ? tree.name : '',
      cards: rawComponents.map((c) => toCard(c, dimensionId)),
      // `category` comes from the framework row; the global tree is the fallback for a
      // dimension the framework query did not carry one for.
      aspect: effectiveAspect(fd.category ?? tree?.category),
      aspectOverride:
        typeof fd.aspect_override === 'string' && fd.aspect_override
          ? (fd.aspect_override as Aspect)
          : null,
    };
  });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const FrameworkDesigner: React.FC = () => {
  const [frameworks, setFrameworks] = useState<FrameworkOption[]>([]);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState<string>('');
  const [dimsTree, setDimsTree] = useState<any[]>([]); // global dimension tree
  const [columns, setColumns] = useState<Column[]>([]);
  const [loading, setLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<Toast>(null);

  // Drag state — id of the card currently being dragged, and hovered column.
  const dragCardId = useRef<string | null>(null);
  const [dragOverDimId, setDragOverDimId] = useState<string | null>(null);

  // UI state
  const [openMenuDimId, setOpenMenuDimId] = useState<string | null>(null);
  const [showAddDimension, setShowAddDimension] = useState(false);
  const [viewCard, setViewCard] = useState<Card | null>(null);

  const selectedFramework = frameworks.find((f) => f.id === selectedFrameworkId) || null;
  const locked = !!selectedFramework && selectedFramework.locked;

  const notifyOk = (message = 'Done') => setToast({ type: 'success', message });
  const notifyErr = (message: string) => setToast({ type: 'error', message });

  // auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2500);
    return () => window.clearTimeout(t);
  }, [toast]);

  // close the column menu on outside click
  useEffect(() => {
    if (!openMenuDimId) return;
    const onDocClick = () => setOpenMenuDimId(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openMenuDimId]);

  // -- Loading ----------------------------------------------------------------

  const loadFrameworks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiCall('/api/frameworks', 'GET');
      const list: FrameworkOption[] = Array.isArray(data)
        ? data.map((f: any) => ({
            id: String(f.id),
            name: f.name || 'Untitled',
            code: f.code || '',
            locked: Number(f.apr_model_count) > 0,
          }))
        : [];
      setFrameworks(list);
      setSelectedFrameworkId((prev) => prev || (list[0] ? list[0].id : ''));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load frameworks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFrameworks();
  }, [loadFrameworks]);

  const loadBoard = useCallback(async (frameworkId: string) => {
    try {
      setBoardLoading(true);
      setError(null);
      const [framework, dims] = await Promise.all([
        apiCall(`/api/frameworks/${frameworkId}`, 'GET'),
        apiCall('/api/dimensions', 'GET'),
      ]);
      const tree: any[] = Array.isArray(dims) ? dims : [];
      const frameworkDims: any[] = Array.isArray(framework?.dimensions) ? framework.dimensions : [];
      const frameworkComponents: any[] = Array.isArray(framework?.components) ? framework.components : [];
      setDimsTree(tree);
      setColumns(buildColumns(frameworkDims, tree, frameworkComponents));
      setDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load board');
    } finally {
      setBoardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedFrameworkId) {
      loadBoard(selectedFrameworkId);
    }
  }, [selectedFrameworkId, loadBoard]);

  // -- Drag & drop ------------------------------------------------------------

  const handleCardDragStart = (e: React.DragEvent, cardId: string) => {
    if (locked) {
      e.preventDefault();
      return;
    }
    dragCardId.current = cardId;
    e.dataTransfer.effectAllowed = 'move';
    // Firefox requires data to be set for a drag to initiate.
    e.dataTransfer.setData('text/plain', cardId);
  };

  const handleCardDragEnd = () => {
    dragCardId.current = null;
    setDragOverDimId(null);
  };

  // Move a card into targetDimId, inserting before beforeCardId (or at the end
  // when beforeCardId is null). Also reassigns the card's dimension.
  const moveCard = (cardId: string, targetDimId: string, beforeCardId: string | null) => {
    if (cardId === beforeCardId) return;
    setColumns((prev) => {
      let moved: Card | undefined;
      const stripped = prev.map((col) => ({
        ...col,
        cards: col.cards.filter((c) => {
          if (c.id === cardId) {
            moved = c;
            return false;
          }
          return true;
        }),
      }));
      if (!moved) return prev;
      const movedCard: Card = { ...moved, dimensionId: targetDimId };
      const next = stripped.map((col) => {
        if (col.dimensionId !== targetDimId) return col;
        const cards = [...col.cards];
        if (beforeCardId == null) {
          cards.push(movedCard);
        } else {
          const idx = cards.findIndex((c) => c.id === beforeCardId);
          if (idx === -1) cards.push(movedCard);
          else cards.splice(idx, 0, movedCard);
        }
        return { ...col, cards };
      });

      /*
       * SAVE THE MOVE IMMEDIATELY.
       *
       * A drag used to set `dirty` and wait for "Save Layout". Every other edit in this
       * product saves as you go, so an unsaved board looked identical to a saved one — you
       * drag a component into Security, open Framework Builder, and it is not there, with
       * nothing anywhere saying why. That reads as a bug in the Builder rather than as work
       * not yet committed.
       */
      void handleSaveLayout(next);
      return next;
    });
  };

  const handleColumnDrop = (e: React.DragEvent, dimId: string) => {
    if (locked) return;
    e.preventDefault();
    setDragOverDimId(null);
    const cardId = dragCardId.current;
    if (cardId) moveCard(cardId, dimId, null); // append to end of column
  };

  const handleCardDrop = (e: React.DragEvent, dimId: string, beforeCardId: string) => {
    if (locked) return;
    e.preventDefault();
    e.stopPropagation(); // don't also trigger the column (append) drop
    setDragOverDimId(null);
    const cardId = dragCardId.current;
    if (cardId) moveCard(cardId, dimId, beforeCardId);
  };

  // -- Save Layout ------------------------------------------------------------

  /**
   * `board` is passed by the autosave, which runs inside the state updater and therefore has
   * the NEW columns while `columns` in this closure is still the pre-move value. Reading state
   * here would persist the board as it was before the drag.
   */
  const handleSaveLayout = async (board?: Column[]) => {
    if (locked) return;
    try {
      setSaving(true);
      const items: Array<{ id: string; dimensionId: string; displayOrder: number }> = [];
      (board ?? columns).forEach((col) => {
        col.cards.forEach((card, index) => {
          items.push({ id: card.id, dimensionId: col.dimensionId, displayOrder: index + 1 });
        });
      });
      /*
       * FRAMEWORK-SCOPED. This used to post to `/api/components/reorder-layout`, which writes
       * `components.dimension_id` — the component's global home — so a drag here moved the
       * component in every other framework too, invisibly, because this board shows one
       * framework at a time.
       */
      await apiCall(`/api/frameworks/${selectedFrameworkId}/components/reorder-layout`, 'POST', {
        items,
      });
      setDirty(false);
      notifyOk('Layout saved');
    } catch (err) {
      // Keep the board as arranged and mark it unsaved, so "Save Layout" becomes a retry
      // rather than the move silently reverting under the user.
      setDirty(true);
      notifyErr(err instanceof Error ? err.message : 'Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  // -- Dimension actions ------------------------------------------------------

  const cardsForDimension = (dimId: string): Card[] => {
    const tree = dimsTree.find((d) => String(d.id) === dimId);
    const rawComponents: any[] = tree && Array.isArray(tree.components) ? tree.components : [];
    return rawComponents.map((c) => toCard(c, dimId));
  };

  const addDimensionToFramework = async (dim: DimOption) => {
    if (locked) return;
    try {
      const created = await apiCall(`/api/frameworks/${selectedFrameworkId}/dimensions`, 'POST', {
        dimension_id: dim.id,
        display_order: columns.length,
        is_mandatory: true,
      });
      const mappingId = created?.id != null ? String(created.id) : `local-fdim-${Date.now()}`;
      setColumns((prev) => [
        ...prev,
        {
          mappingId,
          dimensionId: dim.id,
          name: dim.name,
          cards: cardsForDimension(dim.id),
          aspect: dim.aspect,
          // Added at the library's aspect. An override is a deliberate act on the column.
          aspectOverride: null,
        },
      ]);
      setShowAddDimension(false);
      notifyOk('Dimension added');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to add dimension');
    }
  };

  /**
   * Set — or clear — this framework's override for who answers a dimension.
   *
   * Its own endpoint, because the general dimension-mapping PUT writes display_order,
   * weight_override and is_mandatory unconditionally and would blank all three.
   *
   * Optimistic, then reconciled: the board updates immediately and reverts on failure, so a
   * rejected value cannot sit on screen looking saved.
   */
  const setAspectOverride = async (col: Column, next: Aspect | '') => {
    if (locked) return;
    const override = next === '' ? null : next;
    const previous = col.aspectOverride;
    if (override === previous) return;
    setColumns((prev) =>
      prev.map((c) => (c.dimensionId === col.dimensionId ? { ...c, aspectOverride: override } : c)),
    );
    try {
      await apiCall(
        `/api/frameworks/${selectedFrameworkId}/dimensions/${col.mappingId}/aspect`,
        'PUT',
        { aspect_override: override },
      );
      notifyOk(
        override
          ? `${col.name} is answered by the ${ASPECT_OWNER[override].toLowerCase()} in this framework`
          : `${col.name} follows the library again (${col.aspect})`,
      );
    } catch (err) {
      setColumns((prev) =>
        prev.map((c) =>
          c.dimensionId === col.dimensionId ? { ...c, aspectOverride: previous } : c,
        ),
      );
      notifyErr(err instanceof Error ? err.message : 'Failed to set who answers this dimension');
    }
  };

  const removeDimensionFromFramework = async (col: Column) => {
    setOpenMenuDimId(null);
    if (locked) return;
    if (!window.confirm(`Remove "${col.name}" from this framework? Its components stay in the library.`)) {
      return;
    }
    try {
      await apiCall(`/api/frameworks/${selectedFrameworkId}/dimensions/${col.mappingId}`, 'DELETE');
      setColumns((prev) => prev.filter((c) => c.dimensionId !== col.dimensionId));
      notifyOk('Dimension removed');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to remove dimension');
    }
  };

  // -- Component actions ------------------------------------------------------

  const addComponent = async (dimId: string) => {
    try {
      const created = await apiCall('/api/components', 'POST', {
        name: 'New Component',
        description: '',
        dimensionId: dimId,
        weight: 3,
      });
      const card: Card = {
        id: created?.id != null ? String(created.id) : `local-comp-${Date.now()}`,
        name: typeof created?.name === 'string' ? created.name : 'New Component',
        weight: Number(created?.weight) || 3,
        dimensionId: dimId,
        questions: Array.isArray(created?.questions) ? created.questions.map(toQuestion) : [],
      };
      setColumns((prev) =>
        prev.map((col) => (col.dimensionId === dimId ? { ...col, cards: [...col.cards, card] } : col)),
      );
      notifyOk('Component added');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to add component');
    }
  };

  // Toolbar "+ Add Component" — add to the first dimension column.
  const addComponentToFirst = () => {
    if (columns.length === 0) return;
    addComponent(columns[0]!.dimensionId);
  };

  const deleteComponent = async (card: Card) => {
    if (!window.confirm(`Delete component "${card.name}"? This cannot be undone.`)) return;
    try {
      await apiCall(`/api/components/${card.id}`, 'DELETE');
      setColumns((prev) =>
        prev.map((col) => ({ ...col, cards: col.cards.filter((c) => c.id !== card.id) })),
      );
      notifyOk('Component deleted');
    } catch (err) {
      notifyErr(err instanceof Error ? err.message : 'Failed to delete component');
    }
  };

  // -- Derived ----------------------------------------------------------------

  const usedDimIds = new Set(columns.map((c) => c.dimensionId));
  const availableDimensions: DimOption[] = dimsTree
    .filter((d) => !usedDimIds.has(String(d.id)))
    .map((d) => ({
      id: String(d.id),
      name: typeof d.name === 'string' ? d.name : 'Untitled',
      aspect: effectiveAspect(d.category),
    }));

  const totalComponents = columns.reduce((n, c) => n + c.cards.length, 0);
  const canEdit = !!selectedFrameworkId && !boardLoading;

  // -- Render -----------------------------------------------------------------

  return (
    <div className="page fd-page">
      <HelpPanel help={FrameworkDesignerHelp} />

      <PageHeader
        title="Framework Designer"
        subtitle="Visually compose and manage your scoring dimensions and components"
      />

      {/* Toolbar */}
      <div className="fd-toolbar">
        <button
          type="button"
          className="btn btn-secondary fd-tool-btn"
          onClick={() => setShowAddDimension(true)}
          disabled={!canEdit || locked}
          title={locked ? 'Framework in use — dimension edits disabled' : 'Add a dimension to this framework'}
        >
          <i className="material-icons" aria-hidden="true">add</i>
          Add Dimension
        </button>
        <button
          type="button"
          className="btn btn-secondary fd-tool-btn"
          onClick={addComponentToFirst}
          disabled={!canEdit || locked || columns.length === 0}
          title={locked ? 'Framework in use — edits disabled' : 'Add a component'}
        >
          <i className="material-icons" aria-hidden="true">add</i>
          Add Component
        </button>
        <button
          type="button"
          className="btn btn-primary fd-tool-btn"
          onClick={() => void handleSaveLayout()}
          disabled={!canEdit || locked || saving}
          title={locked ? 'Framework in use — layout locked' : 'Save the current layout'}
        >
          <i className="material-icons" aria-hidden="true">save</i>
          {saving ? 'Saving…' : 'Save Layout'}
        </button>
        {dirty && !locked && <span className="fd-dirty">Unsaved changes</span>}
      </div>

      {/* Framework selector */}
      <div className="fd-selector-card">
        <label className="fd-fw-picker">
          <span>Framework:</span>
          <select
            value={selectedFrameworkId}
            onChange={(e) => setSelectedFrameworkId(e.target.value)}
            aria-label="Select framework"
            disabled={loading || frameworks.length === 0}
          >
            {frameworks.length === 0 && <option value="">No frameworks</option>}
            {frameworks.map((f) => (
              <option key={f.id} value={f.id}>
                {(f.code ? `${f.code} - ` : '') + f.name}
                {f.locked ? ' 🔒' : ''}
              </option>
            ))}
          </select>
        </label>
        {locked && (
          <span className="fd-lock-chip" role="status">
            🔒 Framework in use - Dimension edits disabled
          </span>
        )}
      </div>

      <div className="fd-container">
        {loading ? (
          <div className="fd-state">Loading frameworks…</div>
        ) : error ? (
          <div className="fd-state fd-state-error">
            <p>⚠️ {error}</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => (selectedFrameworkId ? loadBoard(selectedFrameworkId) : loadFrameworks())}
            >
              Retry
            </button>
          </div>
        ) : !selectedFrameworkId ? (
          <div className="fd-state">
            <p>No framework selected. Create a framework first, then compose it here.</p>
          </div>
        ) : boardLoading ? (
          <div className="fd-state">Loading board…</div>
        ) : columns.length === 0 ? (
          <div className="fd-state">
            <p>This framework has no dimensions yet.</p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowAddDimension(true)}
              disabled={locked}
            >
              <i className="material-icons" aria-hidden="true">add</i>
              Add Dimension
            </button>
          </div>
        ) : (
          <div className="fd-board">
            {columns.map((col) => (
              <section
                key={col.dimensionId}
                className={`fd-column ${dragOverDimId === col.dimensionId ? 'fd-column-dragover' : ''}`}
                onDragOver={(e) => {
                  if (locked) return;
                  e.preventDefault();
                  if (dragOverDimId !== col.dimensionId) setDragOverDimId(col.dimensionId);
                }}
                onDragLeave={(e) => {
                  // only clear when the pointer truly leaves the column
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverDimId((prev) => (prev === col.dimensionId ? null : prev));
                  }
                }}
                onDrop={(e) => handleColumnDrop(e, col.dimensionId)}
              >
                <header className="fd-column-header">
                  <div className="fd-column-heading">
                    <div className="fd-column-title-row">
                      <span className="fd-column-name" title={col.name}>
                        {col.name}
                      </span>
                      {locked && (
                        <i className="material-icons fd-column-lock" title="Framework in use" aria-label="Locked">
                          lock
                        </i>
                      )}
                    </div>
                    <span className="fd-column-sub">
                      {col.cards.length} component{col.cards.length === 1 ? '' : 's'}
                    </span>
                    {/*
                      WHO ANSWERS THIS DIMENSION, in this framework.

                      Dimensions are a GLOBAL library — the same row is used by every
                      framework — so the selector edits the framework's OWN override rather
                      than the dimension, and "Inherit" puts it back to the library value.
                      Changing it here therefore cannot alter another framework, which is the
                      whole reason the override exists.
                    */}
                    <select
                      className="fd-aspect-select"
                      style={{
                        color: ASPECT_COLOR[effectiveAspect(col.aspect, col.aspectOverride)].fg,
                        backgroundColor:
                          ASPECT_COLOR[effectiveAspect(col.aspect, col.aspectOverride)].bg,
                      }}
                      value={col.aspectOverride ?? ''}
                      disabled={locked}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setAspectOverride(col, e.target.value as Aspect | '')}
                      title={
                        col.aspectOverride
                          ? `${ASPECT_OWNER[col.aspectOverride]} — set for this framework only. The library says ${col.aspect}.`
                          : `${ASPECT_OWNER[col.aspect]} — ${ASPECT_HINT[col.aspect]}`
                      }
                      aria-label={`Who answers ${col.name} in this framework`}
                    >
                      <option value="">{col.aspect} (inherited)</option>
                      {ASPECTS.map((a) => (
                        <option key={a} value={a}>
                          {a} — {ASPECT_OWNER[a]}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="fd-menu-wrap">
                    <button
                      type="button"
                      className="fd-icon-btn fd-icon-btn-light"
                      title="Column menu"
                      aria-label={`Menu for ${col.name}`}
                      aria-haspopup="true"
                      aria-expanded={openMenuDimId === col.dimensionId}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenMenuDimId((prev) => (prev === col.dimensionId ? null : col.dimensionId));
                      }}
                    >
                      <i className="material-icons">more_vert</i>
                    </button>
                    {openMenuDimId === col.dimensionId && (
                      <div className="fd-menu" role="menu" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="fd-menu-item fd-danger"
                          role="menuitem"
                          disabled={locked}
                          onClick={() => removeDimensionFromFramework(col)}
                        >
                          <i className="material-icons">remove_circle_outline</i>
                          Remove from framework
                        </button>
                      </div>
                    )}
                  </div>
                </header>

                <div className="fd-column-body">
                  {col.cards.length === 0 ? (
                    <div className="fd-empty-drop">
                      {locked ? 'No components' : 'Drop components here'}
                    </div>
                  ) : (
                    col.cards.map((card) => (
                      <article
                        key={card.id}
                        className={`fd-card ${locked ? 'fd-card-locked' : ''}`}
                        draggable={!locked}
                        onDragStart={(e) => handleCardDragStart(e, card.id)}
                        onDragEnd={handleCardDragEnd}
                        onDragOver={(e) => {
                          if (!locked) e.preventDefault();
                        }}
                        onDrop={(e) => handleCardDrop(e, col.dimensionId, card.id)}
                      >
                        <div className="fd-card-main">
                          <span className="fd-card-name" title={card.name}>
                            {card.name}
                          </span>
                          <span
                            className="fd-weight-badge"
                            style={{ backgroundColor: weightColor(card.weight) }}
                            title={`Weight ${card.weight}`}
                            aria-label={`Weight ${card.weight}`}
                          >
                            {card.weight}
                          </span>
                        </div>
                        <div className="fd-card-footer">
                          <span className="fd-q-chip">
                            <i className="material-icons">help_outline</i>
                            {card.questions.length}
                          </span>
                          <div className="fd-card-actions">
                            <button
                              type="button"
                              className="fd-icon-btn"
                              title="View questions"
                              aria-label={`View questions for ${card.name}`}
                              onClick={() => setViewCard(card)}
                            >
                              <i className="material-icons">visibility</i>
                            </button>
                            <button
                              type="button"
                              className="fd-icon-btn fd-danger fd-card-delete"
                              title="Delete component"
                              aria-label={`Delete ${card.name}`}
                              onClick={() => deleteComponent(card)}
                            >
                              <i className="material-icons">delete</i>
                            </button>
                          </div>
                        </div>
                      </article>
                    ))
                  )}
                </div>

                <button
                  type="button"
                  className="fd-add-component"
                  onClick={() => addComponent(col.dimensionId)}
                >
                  + Add component
                </button>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Add Dimension picker modal */}
      {showAddDimension && (
        <div className="fd-modal-overlay" onClick={() => setShowAddDimension(false)}>
          <div
            className="fd-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Add dimension to framework"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fd-modal-header">
              <h2>Add Dimension</h2>
              <button
                type="button"
                className="fd-icon-btn"
                aria-label="Close"
                onClick={() => setShowAddDimension(false)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="fd-modal-body">
              {availableDimensions.length === 0 ? (
                <p className="fd-modal-empty">All dimensions are already in this framework.</p>
              ) : (
                <ul className="fd-dim-list">
                  {availableDimensions.map((d) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        className="fd-dim-option"
                        onClick={() => addDimensionToFramework(d)}
                      >
                        <span>{d.name}</span>
                        {/* Which owner this dimension brings with it — visible BEFORE it is
                            added, since that is the consequence of adding it. */}
                        <span
                          className="fd-dim-option-aspect"
                          style={{
                            color: ASPECT_COLOR[d.aspect].fg,
                            backgroundColor: ASPECT_COLOR[d.aspect].bg,
                          }}
                          title={`${ASPECT_OWNER[d.aspect]} — ${ASPECT_HINT[d.aspect]}`}
                        >
                          {d.aspect}
                        </span>
                        <i className="material-icons">add</i>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View questions modal */}
      {viewCard && (
        <div className="fd-modal-overlay" onClick={() => setViewCard(null)}>
          <div
            className="fd-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`Questions for ${viewCard.name}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fd-modal-header">
              <h2>{viewCard.name}</h2>
              <button
                type="button"
                className="fd-icon-btn"
                aria-label="Close"
                onClick={() => setViewCard(null)}
              >
                <i className="material-icons">close</i>
              </button>
            </div>
            <div className="fd-modal-body">
              {viewCard.questions.length === 0 ? (
                <p className="fd-modal-empty">This component has no questions yet.</p>
              ) : (
                <ol className="fd-q-list">
                  {viewCard.questions.map((q) => (
                    <li key={q.id} className="fd-q-item">
                      <span className="fd-q-text">{q.text}</span>
                      {q.options.length > 0 && (
                        <ul className="fd-q-options">
                          {[...q.options]
                            .sort((a, b) => a.score - b.score)
                            .map((o) => (
                              <li key={o.id}>
                                <span
                                  className="fd-q-score"
                                  style={{ backgroundColor: weightColor(o.score) }}
                                >
                                  {o.score}
                                </span>
                                <span>{o.text}</span>
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`fd-toast fd-toast-${toast.type}`} role="status">
          {toast.type === 'success' ? '✓ ' : '⚠ '}
          {toast.message}
        </div>
      )}

      {/* Footer summary */}
      {!loading && !error && selectedFrameworkId && !boardLoading && columns.length > 0 && (
        <div className="fd-footer-summary">
          {columns.length} dimension{columns.length === 1 ? '' : 's'} · {totalComponents} component
          {totalComponents === 1 ? '' : 's'}
        </div>
      )}
    </div>
  );
};

export default FrameworkDesigner;
